// data/languages/yoruba/categories/grammaire.ts
//
// Les titres de sous-catégories restent en français : GrammarMode les range
// en unités d'après des mots-clés (« pronom », « structure » → unité 1 ;
// « temps verbaux », « négation », « question » → unité 2 ; « préposition »,
// « adjectif », « conjonction », « adverbe » → unité 3).
// Les clés (Mo, ń, kò…) servent aussi de clés dans sentencesgap.ts.

import { GrammarCategory } from '../../../../types/types';

export const grammaire: GrammarCategory = {

  // L'alphabet yoruba a 25 lettres : pas de C, Q, V, X, Z ; GB compte pour une
  // seule lettre ; Ẹ, Ọ et Ṣ portent un point dessous.
  // Sorti de la grammaire dans index.ts : c'est la première étape du parcours.
  'Alphabet yoruba': {
    "A": {
      translation: "a",
      translationEn: "a",
      explanation: "Lettre 1 sur 25. Se prononce « a » comme dans « chat ».",
      explanationEn: "Letter 1 of 25. Pronounced like \"a\" in \"father\".",
      example: "Ajá (chien)",
      exampleEn: "Ajá (dog)",
      audio: "Yo-Lettre-A"
    },
    "B": {
      translation: "bi",
      translationEn: "bi",
      explanation: "Lettre 2 sur 25. Se prononce « b » comme en français.",
      explanationEn: "Letter 2 of 25. Pronounced like English \"b\".",
      example: "Bàbá (père)",
      exampleEn: "Bàbá (father)",
      audio: "Yo-Lettre-B"
    },
    "D": {
      translation: "di",
      translationEn: "di",
      explanation: "Lettre 3 sur 25. Se prononce « d » comme en français.",
      explanationEn: "Letter 3 of 25. Pronounced like English \"d\".",
      example: "Dúdú (noir)",
      exampleEn: "Dúdú (black)",
      audio: "Yo-Lettre-D"
    },
    "E": {
      translation: "é",
      translationEn: "é",
      explanation: "Lettre 4 sur 25. « e » fermé, comme dans « été ».",
      explanationEn: "Letter 4 of 25. Closed \"e\", like \"ay\" in \"say\" (without the glide).",
      example: "Ewé (feuille)",
      exampleEn: "Ewé (leaf)",
      audio: "Yo-Lettre-E"
    },
    "Ẹ": {
      translation: "è",
      translationEn: "è",
      explanation: "Lettre 5 sur 25. « e » ouvert, comme dans « mère ». Le point dessous marque la voyelle ouverte.",
      explanationEn: "Letter 5 of 25. Open \"e\", like in \"bed\". The dot underneath marks the open vowel.",
      example: "Ẹja (poisson)",
      exampleEn: "Ẹja (fish)",
      audio: "Yo-Lettre-E-point"
    },
    "F": {
      translation: "fi",
      translationEn: "fi",
      explanation: "Lettre 6 sur 25. Se prononce « f » comme en français.",
      explanationEn: "Letter 6 of 25. Pronounced like English \"f\".",
      example: "Fìlà (bonnet)",
      exampleEn: "Fìlà (cap)",
      audio: "Yo-Lettre-F"
    },
    "G": {
      translation: "gui",
      translationEn: "gui",
      explanation: "Lettre 7 sur 25. Toujours dur, comme dans « gare », jamais comme dans « girafe ».",
      explanationEn: "Letter 7 of 25. Always hard, like in \"go\".",
      example: "Gààrí (gari (semoule de manioc))",
      exampleEn: "Gààrí (garri (cassava flakes))",
      audio: "Yo-Lettre-G"
    },
    "GB": {
      translation: "gbi",
      translationEn: "gbi",
      explanation: "Lettre 8 sur 25. Une seule lettre : « g » et « b » prononcés en même temps, lèvres fermées.",
      explanationEn: "Letter 8 of 25. A single letter: \"g\" and \"b\" pronounced at the same time, lips closed.",
      example: "Gbogbo (tout)",
      exampleEn: "Gbogbo (all)",
      audio: "Yo-Lettre-GB"
    },
    "H": {
      translation: "hi",
      translationEn: "hi",
      explanation: "Lettre 9 sur 25. Toujours expiré, comme le « h » anglais de « hello ».",
      explanationEn: "Letter 9 of 25. Always breathed, like in \"hello\".",
      example: "Ihò (trou)",
      exampleEn: "Ihò (hole)",
      audio: "Yo-Lettre-H"
    },
    "I": {
      translation: "i",
      translationEn: "i",
      explanation: "Lettre 10 sur 25. Se prononce « i » comme dans « ami ».",
      explanationEn: "Letter 10 of 25. Pronounced like \"ee\" in \"see\".",
      example: "Ilé (maison)",
      exampleEn: "Ilé (house)",
      audio: "Yo-Lettre-I"
    },
    "J": {
      translation: "dji",
      translationEn: "dji",
      explanation: "Lettre 11 sur 25. Se prononce « dj » comme dans « djembé ».",
      explanationEn: "Letter 11 of 25. Pronounced like \"j\" in \"jump\".",
      example: "Jẹun (manger)",
      exampleEn: "Jẹun (to eat)",
      audio: "Yo-Lettre-J"
    },
    "K": {
      translation: "ki",
      translationEn: "ki",
      explanation: "Lettre 12 sur 25. Se prononce « k » comme en français.",
      explanationEn: "Letter 12 of 25. Pronounced like English \"k\".",
      example: "Kẹ̀kẹ́ (vélo)",
      exampleEn: "Kẹ̀kẹ́ (bicycle)",
      audio: "Yo-Lettre-K"
    },
    "L": {
      translation: "li",
      translationEn: "li",
      explanation: "Lettre 13 sur 25. Se prononce « l » comme en français.",
      explanationEn: "Letter 13 of 25. Pronounced like English \"l\".",
      example: "Lọ (aller)",
      exampleEn: "Lọ (to go)",
      audio: "Yo-Lettre-L"
    },
    "M": {
      translation: "mi",
      translationEn: "mi",
      explanation: "Lettre 14 sur 25. Se prononce « m » comme en français.",
      explanationEn: "Letter 14 of 25. Pronounced like English \"m\".",
      example: "Mọ́tò (voiture)",
      exampleEn: "Mọ́tò (car)",
      audio: "Yo-Lettre-M"
    },
    "N": {
      translation: "ni",
      translationEn: "ni",
      explanation: "Lettre 15 sur 25. Se prononce « n ». Seul devant une consonne, il forme une syllabe et porte un ton.",
      explanationEn: "Letter 15 of 25. Pronounced \"n\". On its own before a consonant it forms a syllable and carries a tone.",
      example: "Nǹkan (chose)",
      exampleEn: "Nǹkan (thing)",
      audio: "Yo-Lettre-N"
    },
    "O": {
      translation: "o",
      translationEn: "o",
      explanation: "Lettre 16 sur 25. « o » fermé, comme dans « mot ».",
      explanationEn: "Letter 16 of 25. Closed \"o\", like in \"go\" (without the glide).",
      example: "Owó (argent)",
      exampleEn: "Owó (money)",
      audio: "Yo-Lettre-O"
    },
    "Ọ": {
      translation: "o",
      translationEn: "o",
      explanation: "Lettre 17 sur 25. « o » ouvert, comme dans « porte ». Le point dessous marque la voyelle ouverte.",
      explanationEn: "Letter 17 of 25. Open \"o\", like in \"saw\". The dot underneath marks the open vowel.",
      example: "Ọmọ (enfant)",
      exampleEn: "Ọmọ (child)",
      audio: "Yo-Lettre-O-point"
    },
    "P": {
      translation: "kpi",
      translationEn: "kpi",
      explanation: "Lettre 18 sur 25. Se prononce « kp » : « k » et « p » en même temps. Il n'y a pas de « p » simple en yoruba.",
      explanationEn: "Letter 18 of 25. Pronounced \"kp\": \"k\" and \"p\" at the same time. There is no plain \"p\" in Yoruba.",
      example: "Pupa (rouge)",
      exampleEn: "Pupa (red)",
      audio: "Yo-Lettre-P"
    },
    "R": {
      translation: "ri",
      translationEn: "ri",
      explanation: "Lettre 19 sur 25. « r » roulé du bout de la langue.",
      explanationEn: "Letter 19 of 25. Tapped \"r\" with the tip of the tongue.",
      example: "Rà (acheter)",
      exampleEn: "Rà (to buy)",
      audio: "Yo-Lettre-R"
    },
    "S": {
      translation: "si",
      translationEn: "si",
      explanation: "Lettre 20 sur 25. Toujours « s » comme dans « sel », jamais « z ».",
      explanationEn: "Letter 20 of 25. Always \"s\" as in \"sun\", never \"z\".",
      example: "Sùn (dormir)",
      exampleEn: "Sùn (to sleep)",
      audio: "Yo-Lettre-S"
    },
    "Ṣ": {
      translation: "chi",
      translationEn: "chi",
      explanation: "Lettre 21 sur 25. Se prononce « ch » comme dans « chat ».",
      explanationEn: "Letter 21 of 25. Pronounced like \"sh\" in \"shoe\".",
      example: "Ṣùgbọ́n (mais)",
      exampleEn: "Ṣùgbọ́n (but)",
      audio: "Yo-Lettre-S-point"
    },
    "T": {
      translation: "ti",
      translationEn: "ti",
      explanation: "Lettre 22 sur 25. Se prononce « t » comme en français.",
      explanationEn: "Letter 22 of 25. Pronounced like English \"t\".",
      example: "Tútù (froid)",
      exampleEn: "Tútù (cold)",
      audio: "Yo-Lettre-T"
    },
    "U": {
      translation: "ou",
      translationEn: "ou",
      explanation: "Lettre 23 sur 25. Se prononce « ou » comme dans « loup ».",
      explanationEn: "Letter 23 of 25. Pronounced like \"oo\" in \"food\".",
      example: "Ìlú (ville)",
      exampleEn: "Ìlú (town)",
      audio: "Yo-Lettre-U"
    },
    "W": {
      translation: "wi",
      translationEn: "wi",
      explanation: "Lettre 24 sur 25. Se prononce « w » comme dans « week-end ».",
      explanationEn: "Letter 24 of 25. Pronounced like English \"w\".",
      example: "Wá (venir)",
      exampleEn: "Wá (to come)",
      audio: "Yo-Lettre-W"
    },
    "Y": {
      translation: "yi",
      translationEn: "yi",
      explanation: "Lettre 25 sur 25. Se prononce « y » comme dans « yoga ».",
      explanationEn: "Letter 25 of 25. Pronounced like \"y\" in \"yes\".",
      example: "Yàrá (chambre)",
      exampleEn: "Yàrá (room)",
      audio: "Yo-Lettre-Y"
    },

    // Les trois tons : 3e leçon de l'alphabet (coupure dans LESSON_BREAKS,
    // utils/trailSplit.ts). Chaque fiche réutilise un audio existant.
    // Clés distinctes des mots d'origine (Ajá, Ọ̀kan, Ẹ̀fọn…) : la progression
    // est enregistrée par clé, elle ne doit pas se mélanger avec Animaux, Corps…
    // Traductions toutes différentes : les mauvaises réponses du test sont
    // prises parmi les autres fiches de la leçon.
    "á · ton haut": {
      translation: "Ton haut",
      translationEn: "High tone",
      explanation: "Accent aigu ( ́ ) : la voix monte. Le yoruba a trois tons, et le ton change le sens du mot. Écoute « ajá » (chien) : la 2e syllabe monte.",
      explanationEn: "Acute accent ( ́ ): the voice goes up. Yoruba has three tones, and the tone changes the meaning of the word. Listen to \"ajá\" (dog): the 2nd syllable rises.",
      example: "Ajá (chien) : moyen, puis haut",
      exampleEn: "Ajá (dog): mid, then high",
      audio: "Yo-Aja"
    },
    "a · ton moyen": {
      translation: "Ton moyen",
      translationEn: "Mid tone",
      explanation: "Pas d'accent : la voix reste au milieu, sans monter ni descendre. Écoute « ẹnu » (bouche) : deux tons moyens.",
      explanationEn: "No accent: the voice stays level, neither rising nor falling. Listen to \"ẹnu\" (mouth): two mid tones.",
      example: "Ẹnu (bouche) : moyen, moyen",
      exampleEn: "Ẹnu (mouth): mid, mid",
      audio: "Yo-Enu"
    },
    "à · ton bas": {
      translation: "Ton bas",
      translationEn: "Low tone",
      explanation: "Accent grave ( ̀ ) : la voix descend. Écoute « àyà » (poitrine) : deux tons bas.",
      explanationEn: "Grave accent ( ̀ ): the voice goes down. Listen to \"àyà\" (chest): two low tones.",
      example: "Àyà (poitrine) : bas, bas",
      exampleEn: "Àyà (chest): low, low",
      audio: "Yo-Aya"
    },
    "Ọ̀kan (bas-moyen)": {
      translation: "Un",
      translationEn: "One",
      explanation: "Ọ̀kan : la voix commence bas puis remonte au milieu. À comparer avec ọkàn (cœur) : mêmes lettres, tons inversés.",
      explanationEn: "Ọ̀kan: the voice starts low, then goes back to mid. Compare with ọkàn (heart): same letters, tones reversed.",
      example: "Ọ̀kan (un) ≠ ọkàn (cœur)",
      exampleEn: "Ọ̀kan (one) ≠ ọkàn (heart)",
      audio: "Yo-Ookan"
    },
    "Ọkàn (moyen-bas)": {
      translation: "Cœur",
      translationEn: "Heart",
      explanation: "Ọkàn : la voix commence au milieu puis descend. À comparer avec ọ̀kan (un).",
      explanationEn: "Ọkàn: the voice starts mid, then goes down. Compare with ọ̀kan (one).",
      example: "Ọkàn (cœur) ≠ ọ̀kan (un)",
      exampleEn: "Ọkàn (heart) ≠ ọ̀kan (one)",
      audio: "Yo-Okan-coeur"
    },
    "Ẹ̀fọn (bas-moyen)": {
      translation: "Moustique",
      translationEn: "Mosquito",
      explanation: "Ẹ̀fọn : bas, puis moyen. À comparer avec ẹfọ̀n (buffle) : moyen, puis bas.",
      explanationEn: "Ẹ̀fọn: low, then mid. Compare with ẹfọ̀n (buffalo): mid, then low.",
      example: "Ẹ̀fọn (moustique) ≠ ẹfọ̀n (buffle)",
      exampleEn: "Ẹ̀fọn (mosquito) ≠ ẹfọ̀n (buffalo)",
      audio: "Yo-Efon"
    },
    "Ẹfọ̀n (moyen-bas)": {
      translation: "Buffle",
      translationEn: "Buffalo",
      explanation: "Ẹfọ̀n : moyen, puis bas. À comparer avec ẹ̀fọn (moustique) : bas, puis moyen.",
      explanationEn: "Ẹfọ̀n: mid, then low. Compare with ẹ̀fọn (mosquito): low, then mid.",
      example: "Ẹfọ̀n (buffle) ≠ ẹ̀fọn (moustique)",
      exampleEn: "Ẹfọ̀n (buffalo) ≠ ẹ̀fọn (mosquito)",
      audio: "Yo-Efon-buffle"
    },
    "Ẹ̀yin (bas-moyen)": {
      translation: "Vous",
      translationEn: "You (plural)",
      explanation: "Ẹ̀yin : bas, puis moyen. À comparer avec ẹ̀yìn (dos) : bas, bas. Seule la 2e syllabe change.",
      explanationEn: "Ẹ̀yin: low, then mid. Compare with ẹ̀yìn (back): low, low. Only the 2nd syllable changes.",
      example: "Ẹ̀yin (vous) ≠ ẹ̀yìn (dos)",
      exampleEn: "Ẹ̀yin (you) ≠ ẹ̀yìn (back)",
      audio: "Yo-Eyin"
    },
    "Ẹ̀yìn (bas-bas)": {
      translation: "Dos",
      translationEn: "Back",
      explanation: "Ẹ̀yìn : bas, bas. À comparer avec ẹ̀yin (vous) : bas, puis moyen.",
      explanationEn: "Ẹ̀yìn: low, low. Compare with ẹ̀yin (you): low, then mid.",
      example: "Ẹ̀yìn (dos) ≠ ẹ̀yin (vous)",
      exampleEn: "Ẹ̀yìn (back) ≠ ẹ̀yin (you)",
      audio: "Yo-Eyin-dos"
    },
  },

  // ============================================================
  // UNITÉ 1 — PRONOMS
  // ============================================================

  'Pronoms sujets': {
    'Mo': {
      translation: "Je",
      translationEn: "I",
      explanation: "Pronom sujet de la 1re personne du singulier. Devient « mi » devant la négation « ò » : mi ò…",
      explanationEn: "First person singular subject pronoun. Becomes \"mi\" before the negation \"ò\": mi ò…",
      example: "Mo ń bọ ẹran. (Je fais bouillir de la viande.)",
      exampleEn: "Mo ń bọ ẹran. (I am boiling meat.)",
      audio: "Yo-Mo"
    },
    'O': {
      translation: "Tu",
      translationEn: "You (singular)",
      explanation: "Pronom sujet familier de la 2e personne du singulier (ton moyen). Avec un aîné, on utilise « Ẹ ».",
      explanationEn: "Informal second person singular subject pronoun (mid tone). With an elder, use \"Ẹ\".",
      example: "O ń wẹ̀ l’ọ́wọ́. (Tu es en train de te laver.)",
      exampleEn: "O ń wẹ̀ l’ọ́wọ́. (You are washing.)",
      audio: "Yo-O"
    },
    'Ó': {
      translation: "Il / Elle",
      translationEn: "He / She / It",
      explanation: "Pronom sujet de la 3e personne du singulier (ton haut). Pas de genre : « ó » veut dire il, elle ou ça.",
      explanationEn: "Third person singular subject pronoun (high tone). No gender: \"ó\" means he, she or it.",
      example: "Ó ti fẹ́ jáde. (Il / Elle est sur le point de sortir.)",
      exampleEn: "Ó ti fẹ́ jáde. (He / She is about to go out.)"
    },
    'A': {
      translation: "Nous",
      translationEn: "We",
      explanation: "Pronom sujet de la 1re personne du pluriel.",
      explanationEn: "First person plural subject pronoun.",
      example: "A padà wá s’ílé. (Nous sommes rentrés à la maison.)",
      exampleEn: "A padà wá s’ílé. (We came back home.)",
      audio: "Yo-A"
    },
    'Ẹ': {
      translation: "Vous (pluriel ou politesse)",
      translationEn: "You (plural or polite)",
      explanation: "2e personne du pluriel. Sert aussi à vouvoyer une personne âgée ou respectée : c'est essentiel dans la culture yoruba.",
      explanationEn: "Second person plural. Also used to address an elder or respected person politely, which is essential in Yoruba culture.",
      example: "Ẹ ò lọ mọ́. (Vous n’y allez plus.)",
      exampleEn: "Ẹ ò lọ mọ́. (You no longer go there.)",
      audio: "Yo-E"
    },
    'Wọ́n': {
      translation: "Ils / Elles",
      translationEn: "They",
      explanation: "Pronom sujet de la 3e personne du pluriel. Sert aussi, par respect, à parler d'un aîné.",
      explanationEn: "Third person plural subject pronoun. Also used respectfully to refer to an elder.",
      example: "Wọ́n jẹ gbogbo ẹ̀ tán. (Ils ont tout mangé.)",
      exampleEn: "Wọ́n jẹ gbogbo ẹ̀ tán. (They ate it all.)",
      audio: "Yo-Won"
    },
  },

  'Pronoms emphatiques': {
    'Èmi': {
      translation: "Moi / Me",
      translationEn: "Me",
      explanation: "Forme longue, pour insister ou répondre seul. S'emploie aussi devant « yóò » (futur) et « kò » (négation).",
      explanationEn: "Long form, used for emphasis or on its own. Also used before \"yóò\" (future) and \"kò\" (negation).",
      example: "Èmi àti Bọ́lá pàdé. (Bola et moi nous sommes rencontrés.)",
      exampleEn: "Èmi àti Bọ́lá pàdé. (Bola and I met.)",
      audio: "Yo-Emi"
    },
    'Ìwọ': {
      translation: "Toi / Te",
      translationEn: "You",
      explanation: "Forme longue de « o ».",
      explanationEn: "Long form of \"o\".",
      example: "Ìwọ ń kọ́? (Et toi ?)",
      exampleEn: "Ìwọ ń kọ́? (And you?)"
    },
    'Òun': {
      translation: "Lui / Elle / Le / La",
      translationEn: "Him / Her",
      explanation: "Forme longue de « ó ».",
      explanationEn: "Long form of \"ó\".",
      example: "Òun ló ní i. (C’est à lui / à elle.)",
      exampleEn: "Òun ló ní i. (It’s his / hers.)",
      audio: "Yo-Oun"
    },
    'Àwa': {
      translation: "Nous",
      translationEn: "Us",
      explanation: "Forme longue de « a ».",
      explanationEn: "Long form of \"a\".",
      example: "Wọ́n fún (à)wa ní ẹ̀bùn. (Ils nous ont offert un cadeau.)",
      exampleEn: "Wọ́n fún (à)wa ní ẹ̀bùn. (They gave us a gift.)",
      audio: "Yo-Awa"
    },
    'Ẹ̀yin': {
      translation: "Vous",
      translationEn: "You (plural)",
      explanation: "Forme longue de « ẹ ».",
      explanationEn: "Long form of \"ẹ\".",
      example: "Ẹ̀yin ni mo sọ fún. (C’est à vous que je l’ai dit.)",
      exampleEn: "Ẹ̀yin ni mo sọ fún. (It’s you I told.)",
      audio: "Yo-Eyin"
    },
    'Àwọn': {
      translation: "Eux / Elles / Les",
      translationEn: "Them",
      explanation: "Forme longue de « wọ́n ». Devant un nom, « àwọn » marque aussi le pluriel : àwọn ọmọ = les enfants.",
      explanationEn: "Long form of \"wọ́n\". Before a noun, \"àwọn\" also marks the plural: àwọn ọmọ = the children.",
      example: "Àwọn ni ọ̀rẹ́ mi. (Ce sont mes amis.)",
      exampleEn: "Àwọn ni ọ̀rẹ́ mi. (They are my friends.)",
      audio: "Yo-Awon"
    },
  },

  'Pronoms possessifs': {
    'mi': {
      translation: "Mon / Ma / Mes",
      translationEn: "My",
      explanation: "Le possessif se place APRÈS le nom : ilé mi = ma maison (litt. maison moi).",
      explanationEn: "The possessive comes AFTER the noun: ilé mi = my house (lit. house me).",
      example: "Orúkọ mi ni Adé. (Je m'appelle Adé.)",
      exampleEn: "Orúkọ mi ni Adé. (My name is Adé.)"
    },
    'rẹ': {
      translation: "Ton / Ta / Tes",
      translationEn: "Your (singular)",
      explanation: "Possessif de la 2e personne du singulier (ton moyen). Ne pas confondre avec « rẹ̀ » (son/sa), au ton bas.",
      explanationEn: "Second person singular possessive (mid tone). Don't confuse it with \"rẹ̀\" (his/her), which has a low tone.",
      example: "Kí ni orúkọ rẹ? (Comment t'appelles-tu ?)",
      exampleEn: "Kí ni orúkọ rẹ? (What is your name?)"
    },
    'rẹ̀': {
      translation: "Son / Sa / Ses",
      translationEn: "His / Her / Its",
      explanation: "Possessif de la 3e personne du singulier (ton bas).",
      explanationEn: "Third person singular possessive (low tone).",
      example: "Ọmọ rẹ̀ kéré. (Son enfant est petit.)",
      exampleEn: "Ọmọ rẹ̀ kéré. (His/Her child is small.)"
    },
    'wa': {
      translation: "Notre / Nos",
      translationEn: "Our",
      explanation: "Possessif de la 1re personne du pluriel.",
      explanationEn: "First person plural possessive.",
      example: "Ìlú wa lẹ́wà. (Notre ville est belle.)",
      exampleEn: "Ìlú wa lẹ́wà. (Our town is beautiful.)"
    },
    'yín': {
      translation: "Votre / Vos",
      translationEn: "Your (plural or polite)",
      explanation: "Possessif de la 2e personne du pluriel, ou de politesse.",
      explanationEn: "Second person plural possessive, also used politely.",
      example: "Kí ni orúkọ yín? (Comment vous appelez-vous ?)",
      exampleEn: "Kí ni orúkọ yín? (What is your name? — polite)"
    },
    'wọn': {
      translation: "Leur / Leurs",
      translationEn: "Their",
      explanation: "Possessif de la 3e personne du pluriel.",
      explanationEn: "Third person plural possessive.",
      example: "Ilé wọn tóbi. (Leur maison est grande.)",
      exampleEn: "Ilé wọn tóbi. (Their house is big.)"
    },
  },

  // Pronoms possessifs autonomes : Tí (« celui de ») + pronom emphatique.
  // Ils remplacent le nom (le mien, le tien…), à l'inverse de mi, rẹ… qui le suivent.
  'Pronoms possessifs — Le mien, le tien': {
    "T’èmi": {
      translation: "Le mien / À moi",
      translationEn: "Mine",
      explanation: "Tí (« celui de ») + èmi (moi). En se collant au pronom, tí perd son « í » : on ne dit pas « tí èmi » mais t’èmi. L’apostrophe marque la voyelle disparue. « … ni » = « c’est … ».",
      explanationEn: "Tí (\"the one of\") + èmi (me). When it joins the pronoun, tí loses its \"í\": you don't say \"tí èmi\" but t’èmi. The apostrophe marks the dropped vowel. \"… ni\" = \"it is …\".",
      example: "T’èmi ni (C’est à moi.)",
      exampleEn: "T’èmi ni (It’s mine.)",
      audio: "Yo-Temi"
    },
    "T’ìwọ": {
      translation: "Le tien / À toi",
      translationEn: "Yours (singular)",
      explanation: "Tí + ìwọ (toi). Pour « le tien », on peut dire t’ìwọ ou t’ìrẹ : même sens. « … nìyẹn » = « c’est … (ça) ».",
      explanationEn: "Tí + ìwọ (you). For \"yours\", you can say t’ìwọ or t’ìrẹ: same meaning. \"… nìyẹn\" = \"that is …\".",
      example: "T’ìwọ nìyẹn (C’est à toi.)",
      exampleEn: "T’ìwọ nìyẹn (That’s yours.)",
      audio: "Yo-Tiwo"
    },
    "T’ìrẹ": {
      translation: "Le tien / À toi (variante)",
      translationEn: "Yours (singular, variant)",
      explanation: "Tí + ìrẹ (toi). Deuxième forme de « le tien » : t’ìrẹ et t’ìwọ ont le même sens.",
      explanationEn: "Tí + ìrẹ (you). Second form of \"yours\": t’ìrẹ and t’ìwọ mean the same.",
      example: "Même sens que t’ìwọ : T’ìwọ nìyẹn (C’est à toi.)",
      exampleEn: "Same meaning as t’ìwọ: T’ìwọ nìyẹn (That’s yours.)",
      audio: "Yo-Tire"
    },
    "T’òun": {
      translation: "Le sien / À lui / À elle",
      translationEn: "His / Hers",
      explanation: "Tí + òun (lui / elle). Pas de masculin ni de féminin : t’òun veut dire aussi bien « à lui » que « à elle ». « … kọ́ » = « ce n’est pas … ».",
      explanationEn: "Tí + òun (him / her). No masculine or feminine: t’òun means both \"his\" and \"hers\". \"… kọ́\" = \"it is not …\".",
      example: "T’òun kọ́ (Ce n’est pas à lui / à elle.)",
      exampleEn: "T’òun kọ́ (It’s not his / hers.)",
      audio: "Yo-Toun"
    },
    "T’àwa": {
      translation: "Le nôtre / À nous",
      translationEn: "Ours",
      explanation: "Tí + àwa (nous). « … l’èyí » = « ceci est … ».",
      explanationEn: "Tí + àwa (us). \"… l’èyí\" = \"this is …\".",
      example: "T’àwa l’èyí (Ceci est à nous.)",
      exampleEn: "T’àwa l’èyí (This is ours.)",
      audio: "Yo-Tawa"
    },
    "T’ẹ̀yin": {
      translation: "Le vôtre / À vous",
      translationEn: "Yours (plural / polite)",
      explanation: "Tí + ẹ̀yin (vous). « … ni » = « c’est … ».",
      explanationEn: "Tí + ẹ̀yin (you, plural). \"… ni\" = \"it is …\".",
      example: "T’ẹ̀yin ni (C’est à vous.)",
      exampleEn: "T’ẹ̀yin ni (It’s yours.)",
      audio: "Yo-Teyin"
    },
    "T’àwọn": {
      translation: "Le leur / À eux",
      translationEn: "Theirs",
      explanation: "Tí + àwọn (eux / elles). « … kọ́ » = « ce n’est pas … ».",
      explanationEn: "Tí + àwọn (them). \"… kọ́\" = \"it is not …\".",
      example: "T’àwọn kọ́ (Ce n’est pas à eux.)",
      exampleEn: "T’àwọn kọ́ (It’s not theirs.)",
      audio: "Yo-Tawon"
    },
  },

  'Pronoms démonstratifs et pluriel': {
    'yìí': {
      translation: "Ce… -ci / Cette… -ci",
      translationEn: "This",
      explanation: "Se place après le nom. « Èyí » s'emploie seul : èyí = ceci.",
      explanationEn: "Comes after the noun. \"Èyí\" is used on its own: èyí = this one.",
      example: "Ilé yìí tóbi. (Cette maison est grande.)",
      exampleEn: "Ilé yìí tóbi. (This house is big.)"
    },
    'yẹn': {
      translation: "Ce… -là / Cette… -là",
      translationEn: "That",
      explanation: "Se place après le nom. « Ìyẹn » s'emploie seul : ìyẹn = cela.",
      explanationEn: "Comes after the noun. \"Ìyẹn\" is used on its own: ìyẹn = that one.",
      example: "Ọkùnrin yẹn ga. (Cet homme-là est grand.)",
      exampleEn: "Ọkùnrin yẹn ga. (That man is tall.)"
    },
    'náà': {
      translation: "Le / La (déjà mentionné)",
      translationEn: "The (already mentioned)",
      explanation: "Le yoruba n'a pas d'article. « Náà » après le nom désigne une chose connue. Avant le verbe, il veut dire « aussi ».",
      explanationEn: "Yoruba has no articles. \"Náà\" after a noun points to something known. Before a verb, it means \"also\".",
      example: "Ìwé náà dára. (Le livre est bien.)",
      exampleEn: "Ìwé náà dára. (The book is good.)"
    },
    'àwọn': {
      translation: "Les / Des (pluriel)",
      translationEn: "Plural marker",
      explanation: "Les noms ne changent pas au pluriel. On ajoute « àwọn » devant le nom quand il faut préciser.",
      explanationEn: "Nouns don't change in the plural. Add \"àwọn\" before the noun when needed.",
      example: "Àwọn ọmọ ń ṣeré. (Les enfants jouent.)",
      exampleEn: "Àwọn ọmọ ń ṣeré. (The children are playing.)"
    },
  },

  // ============================================================
  // UNITÉ 2 — TEMPS, NÉGATION & QUESTIONS
  // ============================================================

  'Temps verbaux — Présent et passé': {
    'ń': {
      translation: "Présent (action en cours ou habitude)",
      translationEn: "Present (ongoing or habitual action)",
      explanation: "« Ń » se place entre le sujet et le verbe. Il équivaut à « être en train de ».",
      explanationEn: "\"Ń\" goes between the subject and the verb. It means \"to be doing\".",
      example: "Mo ń lọ sí ọjà. (Je vais au marché.)",
      exampleEn: "Mo ń lọ sí ọjà. (I am going to the market.)"
    },
    'ti': {
      translation: "Passé composé (action accomplie)",
      translationEn: "Perfect (completed action)",
      explanation: "« Ti » indique qu'une action est terminée. Sans particule, un verbe d'action est aussi au passé : « Mo lọ » = je suis allé.",
      explanationEn: "\"Ti\" shows that an action is completed. With no particle, an action verb is also in the past: \"Mo lọ\" = I went.",
      example: "Mo ti jẹun. (J'ai déjà mangé.)",
      exampleEn: "Mo ti jẹun. (I have eaten.)"
    },
    'máa ń': {
      translation: "Habitude (d'habitude, souvent)",
      translationEn: "Habitual (usually, often)",
      explanation: "« Máa ń » insiste sur une action répétée.",
      explanationEn: "\"Máa ń\" stresses a repeated action.",
      example: "Mo máa ń jí ní àárọ̀. (Je me réveille d'habitude le matin.)",
      exampleEn: "Mo máa ń jí ní àárọ̀. (I usually wake up in the morning.)"
    },
  },

  'Temps verbaux — Futur et impératif': {
    'máa': {
      translation: "Futur (courant)",
      translationEn: "Future (everyday)",
      explanation: "« Máa » sans « ń » exprime le futur. À la 1re personne, « mo máa » se contracte en « màá ».",
      explanationEn: "\"Máa\" without \"ń\" expresses the future. In the first person, \"mo máa\" contracts to \"màá\".",
      example: "Ó máa wá ní ọ̀la. (Il/Elle viendra demain.)",
      exampleEn: "Ó máa wá ní ọ̀la. (He/She will come tomorrow.)"
    },
    'Màá': {
      translation: "Futur (je vais… / je ferai…)",
      translationEn: "Future (I will… / I'm going to…)",
      explanation: "Contraction de « mo máa » (je + futur). On entend aussi « mo màá ». Ton bas puis haut, à ne pas confondre avec « má » (interdiction).",
      explanationEn: "Contraction of \"mo máa\" (I + future). You also hear \"mo màá\". Low then high tone, not to be confused with \"má\" (prohibition).",
      example: "Màá gbìyànjú. (J’essaierai.)",
      exampleEn: "Màá gbìyànjú. (I will try.)",
      audio: "Yo-Maa"
    },
    'yóò': {
      translation: "Futur (soutenu)",
      translationEn: "Future (formal)",
      explanation: "Futur plus soutenu. Avec « je », on dit « èmi yóò » (et non « mo yóò »). À la 3e personne, « yóò » s'emploie seul.",
      explanationEn: "More formal future. With \"I\", say \"èmi yóò\" (not \"mo yóò\"). In the third person, \"yóò\" stands alone.",
      example: "Èmi yóò lọ. (J'irai.)",
      exampleEn: "Èmi yóò lọ. (I will go.)"
    },
    'Impératif': {
      translation: "Le verbe seul donne un ordre",
      translationEn: "The bare verb gives an order",
      explanation: "Pour donner un ordre, on utilise le verbe seul. Ajoutez « ẹ » devant pour parler poliment ou à plusieurs personnes.",
      explanationEn: "To give an order, use the bare verb. Put \"ẹ\" in front to be polite or to address several people.",
      example: "Jókòó! (Assieds-toi !) · Ẹ jókòó. (Asseyez-vous.)",
      exampleEn: "Jókòó! (Sit down!) · Ẹ jókòó. (Please sit down.)"
    },
    'Ẹ jẹ́ ká': {
      translation: "Allons… / Faisons…",
      translationEn: "Let's…",
      explanation: "« Ẹ jẹ́ ká » + verbe sert à proposer de faire quelque chose ensemble.",
      explanationEn: "\"Ẹ jẹ́ ká\" + verb suggests doing something together.",
      example: "Ẹ jẹ́ ká lọ. (Allons-y.)",
      exampleEn: "Ẹ jẹ́ ká lọ. (Let's go.)"
    },
  },

  'Négation': {
    'kò': {
      translation: "Ne… pas",
      translationEn: "Not",
      explanation: "« Kò » se place avant le verbe. Avec « ń », on dit « kò… » sans « ń » : ó ń lọ → kò lọ (il ne va pas).",
      explanationEn: "\"Kò\" comes before the verb. With \"ń\", drop it: ó ń lọ → kò lọ (he isn't going).",
      example: "Bàbá kò sí nílé. (Papa n'est pas à la maison.)",
      exampleEn: "Bàbá kò sí nílé. (Father is not at home.)"
    },
    'ò': {
      translation: "Ne… pas (forme courte, à l'oral)",
      translationEn: "Not (short spoken form)",
      explanation: "À l'oral, « kò » devient souvent « ò ». « Mo » devient alors « mi » : mi ò…",
      explanationEn: "In speech, \"kò\" often becomes \"ò\". \"Mo\" then becomes \"mi\": mi ò…",
      example: "Mi ò mọ̀. (Je ne sais pas.)",
      exampleEn: "Mi ò mọ̀. (I don't know.)"
    },
    'kò tíì': {
      translation: "Pas encore",
      translationEn: "Not yet",
      explanation: "Négation de « ti » (action accomplie).",
      explanationEn: "Negative of \"ti\" (completed action).",
      example: "Kò tíì dé. (Il/Elle n'est pas encore arrivé·e.)",
      exampleEn: "Kò tíì dé. (He/She hasn't arrived yet.)"
    },
    'má': {
      translation: "Ne… pas (interdiction)",
      translationEn: "Don't (prohibition)",
      explanation: "« Má » + verbe sert à interdire : má lọ = ne pars pas. Ton haut, à ne pas confondre avec « màá » (futur).",
      explanationEn: "\"Má\" + verb is used to forbid something: má lọ = don't go. High tone, not to be confused with \"màá\" (future).",
      example: "Má gbàgbé! (N’oublie pas !)",
      exampleEn: "Má gbàgbé! (Don't forget!)",
      audio: "Yo-Ma"
    },
    'kì í': {
      translation: "Ne… jamais / pas d'habitude",
      translationEn: "Never / Not usually",
      explanation: "Négation de l'habitude (« máa ń »).",
      explanationEn: "Negative of the habitual (\"máa ń\").",
      example: "Mi kì í mu ọtí. (Je ne bois pas d'alcool.)",
      exampleEn: "Mi kì í mu ọtí. (I don't drink alcohol.)"
    },
  },

  'Questions': {
    'Ṣé': {
      translation: "Est-ce que… ?",
      translationEn: "Yes/no question marker",
      explanation: "« Ṣé » en début de phrase transforme une affirmation en question fermée.",
      explanationEn: "\"Ṣé\" at the start of a sentence turns a statement into a yes/no question.",
      example: "Ṣé o ti jẹun? (As-tu mangé ?)",
      exampleEn: "Ṣé o ti jẹun? (Have you eaten?)"
    },
    'Kí ni': {
      translation: "Que / Quoi ?",
      translationEn: "What?",
      explanation: "Souvent contracté en « kí ló » ou « kíni ».",
      explanationEn: "Often shortened to \"kí ló\" or \"kíni\".",
      example: "Kí ni orúkọ ẹ? (Comment tu t’appelles ?)",
      exampleEn: "Kí ni orúkọ ẹ? (What's your name?)",
      audio: "Yo-Ki_ni"
    },
    'Ta ni': {
      translation: "Qui ?",
      translationEn: "Who?",
      explanation: "Se place en tête de phrase.",
      explanationEn: "Comes at the start of the sentence.",
      example: "Tani yẹn? (Qui est-ce ?)",
      exampleEn: "Tani yẹn? (Who is that?)"
    },
    'Níbo': {
      translation: "Où ?",
      translationEn: "Where?",
      explanation: "On dit aussi « … dà? » : ìyá dà? = où est maman ?",
      explanationEn: "You can also say \"… dà?\": ìyá dà? = where is mother?",
      example: "Níbo ni ọjà wà? (Où est le marché ?)",
      exampleEn: "Níbo ni ọjà wà? (Where is the market?)"
    },
    'Báwo': {
      translation: "Comment ?",
      translationEn: "How?",
      explanation: "« Báwo ni? » est aussi la salutation la plus courante.",
      explanationEn: "\"Báwo ni?\" is also the most common greeting.",
      example: "Báwo ni iṣẹ́? (Comment va le travail ?)",
      exampleEn: "Báwo ni iṣẹ́? (How is work?)",
      audio: "Yo-Bawo"
    },
    'Èló': {
      translation: "Combien ? (prix)",
      translationEn: "How much? (price)",
      explanation: "Pour demander un prix.",
      explanationEn: "Used to ask a price.",
      example: "Èló ni èyí? (Combien coûte ceci ?)",
      exampleEn: "Èló ni èyí? (How much is this?)",
      audio: "Yo-Elo"
    },
    'Mélòó': {
      translation: "Combien ? (nombre)",
      translationEn: "How many?",
      explanation: "Se place après le nom compté.",
      explanationEn: "Comes after the noun being counted.",
      example: "Ọmọ mélòó ni o ní? (Combien d'enfants as-tu ?)",
      exampleEn: "Ọmọ mélòó ni o ní? (How many children do you have?)",
      audio: "Yo-Meloo"
    },
    'Nígbà wo': {
      translation: "Quand ?",
      translationEn: "When?",
      explanation: "Litt. « à quel moment ».",
      explanationEn: "Lit. \"at which time\".",
      example: "Nígbà wo lo ń jáde? (Quand sors-tu ?)",
      exampleEn: "Nígbà wo lo ń jáde? (When are you going out?)"
    },
    'Kí nìdí': {
      translation: "Pourquoi ?",
      translationEn: "Why?",
      explanation: "Suivi de « tí… fi » : kí nìdí tí o fi… = pourquoi est-ce que tu…",
      explanationEn: "Followed by \"tí… fi\": kí nìdí tí o fi… = why do you…",
      example: "Kí nìdí tí o fi ń sunkún? (Pourquoi pleures-tu ?)",
      exampleEn: "Kí nìdí tí o fi ń sunkún? (Why are you crying?)"
    },
  },

  // ============================================================
  // UNITÉ 3 — PRÉPOSITIONS, ADJECTIFS, CONJONCTIONS & ADVERBES
  // ============================================================

  'Prépositions de lieu': {
    'ní': {
      translation: "À / Dans / En (lieu)",
      translationEn: "At / In (location)",
      explanation: "Indique où l'on est. Se contracte souvent avec le nom : ní ilé → nílé.",
      explanationEn: "Shows where something is. Often merges with the noun: ní ilé → nílé.",
      example: "Mo wà ní ọjà. (Je suis au marché.)",
      exampleEn: "Mo wà ní ọjà. (I am at the market.)"
    },
    'sí': {
      translation: "À / Vers (direction)",
      translationEn: "To / Towards",
      explanation: "Indique où l'on va, après un verbe de mouvement.",
      explanationEn: "Shows where you are going, after a verb of movement.",
      example: "Mo ń lọ sí ilé. (Je rentre à la maison.)",
      exampleEn: "Mo ń lọ sí ilé. (I am going home.)"
    },
    'nínú': {
      translation: "Dans / À l'intérieur de",
      translationEn: "Inside",
      explanation: "Litt. « au ventre de » (inú = intérieur).",
      explanationEn: "Lit. \"in the belly of\" (inú = inside).",
      example: "Omi wà nínú ìgò. (L'eau est dans la bouteille.)",
      exampleEn: "Omi wà nínú ìgò. (The water is in the bottle.)"
    },
    'lórí': {
      translation: "Sur",
      translationEn: "On",
      explanation: "Litt. « à la tête de » (orí = tête).",
      explanationEn: "Lit. \"at the head of\" (orí = head).",
      example: "Ìwé wà lórí tábìlì. (Le livre est sur la table.)",
      exampleEn: "Ìwé wà lórí tábìlì. (The book is on the table.)"
    },
    'lábẹ́': {
      translation: "Sous",
      translationEn: "Under",
      explanation: "Litt. « au-dessous de » (abẹ́ = dessous).",
      explanationEn: "Lit. \"at the underside of\" (abẹ́ = underside).",
      example: "Ológbò wà lábẹ́ àga. (Le chat est sous la chaise.)",
      exampleEn: "Ológbò wà lábẹ́ àga. (The cat is under the chair.)"
    },
    'lẹ́gbẹ̀ẹ́': {
      translation: "À côté de",
      translationEn: "Beside",
      explanation: "Litt. « au flanc de ».",
      explanationEn: "Lit. \"at the side of\".",
      example: "Ilé wa wà lẹ́gbẹ̀ẹ́ ọjà. (Notre maison est à côté du marché.)",
      exampleEn: "Ilé wa wà lẹ́gbẹ̀ẹ́ ọjà. (Our house is next to the market.)"
    },
    'láti': {
      translation: "De / Depuis (origine)",
      translationEn: "From",
      explanation: "Indique le point de départ.",
      explanationEn: "Shows the starting point.",
      example: "Mo wá láti Èkó. (Je viens de Lagos.)",
      exampleEn: "Mo wá láti Èkó. (I come from Lagos.)"
    },
  },

  'Prépositions de relation': {
    'pẹ̀lú': {
      translation: "Avec",
      translationEn: "With",
      explanation: "Indique l'accompagnement.",
      explanationEn: "Shows accompaniment.",
      example: "Mo lọ pẹ̀lú ọ̀rẹ́ mi. (Je suis allé avec mon ami.)",
      exampleEn: "Mo lọ pẹ̀lú ọ̀rẹ́ mi. (I went with my friend.)"
    },
    'fún': {
      translation: "Pour / À (destinataire)",
      translationEn: "For / To (recipient)",
      explanation: "Indique à qui l'on destine quelque chose.",
      explanationEn: "Shows who something is for.",
      example: "Mo ra ẹ̀bùn fún ìyá mi. (J'ai acheté un cadeau pour ma mère.)",
      exampleEn: "Mo ra ẹ̀bùn fún ìyá mi. (I bought a gift for my mother.)"
    },
    'ṣáájú': {
      translation: "Avant",
      translationEn: "Before",
      explanation: "Indique ce qui vient avant.",
      explanationEn: "Shows what comes before.",
      example: "Wẹ ọwọ́ ṣáájú oúnjẹ. (Lave-toi les mains avant le repas.)",
      exampleEn: "Wẹ ọwọ́ ṣáájú oúnjẹ. (Wash your hands before the meal.)"
    },
    'lẹ́yìn': {
      translation: "Après / Derrière",
      translationEn: "After / Behind",
      explanation: "Litt. « au dos de » (ẹ̀yìn = dos).",
      explanationEn: "Lit. \"at the back of\" (ẹ̀yìn = back).",
      example: "Lẹ́yìn iṣẹ́, màá lọ sílé. (Après le travail, je rentrerai.)",
      exampleEn: "Lẹ́yìn iṣẹ́, màá lọ sílé. (After work, I will go home.)"
    },
  },

  'Adjectifs — Qualités et états': {
    "dáa": {
      translation: "Bon",
      translationEn: "Good",
      explanation: "Forme courte de « dára », très courante à l’oral.",
      explanationEn: "Short form of \"dára\", very common in speech.",
      example: "Ó dáa. (C’est bon.)",
      exampleEn: "Ó dáa. (It’s good.)",
      audio: "Yo-Daa"
    },
    "burú": {
      translation: "Mauvais",
      translationEn: "Bad",
      explanation: "Verbe d’état : s’emploie seul, sans « être ».",
      explanationEn: "Stative verb: used on its own, without \"to be\".",
      example: "Iṣẹ́ yìí burú. (Ce travail est mauvais.)",
      exampleEn: "Iṣẹ́ yìí burú. (This work is bad.)",
      audio: "Yo-Buru"
    },
    "kéré": {
      translation: "Petit",
      translationEn: "Small",
      explanation: "Verbe d’état : s’emploie seul, sans « être ».",
      explanationEn: "Stative verb: used on its own, without \"to be\".",
      example: "Yàrá mi kéré. (Ma chambre est petite.)",
      exampleEn: "Yàrá mi kéré. (My room is small.)"
    },
    "ga": {
      translation: "Grand (de taille)",
      translationEn: "Tall",
      explanation: "Verbe d’état : s’emploie seul, sans « être ».",
      explanationEn: "Stative verb: used on its own, without \"to be\".",
      example: "Bàbá mi ga. (Mon père est grand.)",
      exampleEn: "Bàbá mi ga. (My father is tall.)",
      audio: "Yo-Ga"
    },
    "kúrú": {
      translation: "Court / Petit (de taille)",
      translationEn: "Short",
      explanation: "Verbe d’état : s’emploie seul, sans « être ».",
      explanationEn: "Stative verb: used on its own, without \"to be\".",
      example: "Ọmọ náà kúrú. (L’enfant est petit.)",
      exampleEn: "Ọmọ náà kúrú. (The child is short.)",
      audio: "Yo-Kuru"
    },
    "wúwo": {
      translation: "Lourd",
      translationEn: "Heavy",
      explanation: "Verbe d’état : s’emploie seul, sans « être ».",
      explanationEn: "Stative verb: used on its own, without \"to be\".",
      example: "Àpò yìí wúwo. (Ce sac est lourd.)",
      exampleEn: "Àpò yìí wúwo. (This bag is heavy.)"
    },
    "fúyé": {
      translation: "Léger",
      translationEn: "Light",
      explanation: "Verbe d’état : s’emploie seul, sans « être ».",
      explanationEn: "Stative verb: used on its own, without \"to be\".",
      example: "Àpò yìí fúyé. (Ce sac est léger.)",
      exampleEn: "Àpò yìí fúyé. (This bag is light.)"
    },
    "félé": {
      translation: "Léger / Mince",
      translationEn: "Thin / Light",
      explanation: "Verbe d’état : s’emploie seul, sans « être ».",
      explanationEn: "Stative verb: used on its own, without \"to be\".",
      example: "Aṣọ yìí félé. (Ce tissu est fin.)",
      exampleEn: "Aṣọ yìí félé. (This cloth is thin.)"
    },
    "fífẹ̀": {
      translation: "Large",
      translationEn: "Wide",
      explanation: "Se place après le nom.",
      explanationEn: "Comes after the noun.",
      example: "Ọ̀nà fífẹ̀. (Une route large.)",
      exampleEn: "Ọ̀nà fífẹ̀. (A wide road.)"
    },
    "mọ́": {
      translation: "Propre",
      translationEn: "Clean",
      explanation: "Verbe d’état : s’emploie seul, sans « être ».",
      explanationEn: "Stative verb: used on its own, without \"to be\".",
      example: "Yàrá yìí mọ́. (Cette chambre est propre.)",
      exampleEn: "Yàrá yìí mọ́. (This room is clean.)"
    },
    "dọ̀tí": {
      translation: "Sale",
      translationEn: "Dirty",
      explanation: "Verbe d’état : s’emploie seul, sans « être ».",
      explanationEn: "Stative verb: used on its own, without \"to be\".",
      example: "Aṣọ mi dọ̀tí. (Mon vêtement est sale.)",
      exampleEn: "Aṣọ mi dọ̀tí. (My clothes are dirty.)"
    },
    "òde-òní": {
      translation: "Moderne",
      translationEn: "Modern",
      explanation: "Se place après le nom.",
      explanationEn: "Comes after the noun.",
      example: "Ilé òde-òní. (Une maison moderne.)",
      exampleEn: "Ilé òde-òní. (A modern house.)"
    },
    "wọ́n": {
      translation: "Cher",
      translationEn: "Expensive",
      explanation: "Verbe d’état : s’emploie seul, sans « être ».",
      explanationEn: "Stative verb: used on its own, without \"to be\".",
      example: "Ilé yìí wọ́n. (Cette maison est chère.)",
      exampleEn: "Ilé yìí wọ́n. (This house is expensive.)",
      audio: "Yo-Won"
    },
    "gbóná": {
      translation: "Chaud",
      translationEn: "Hot",
      explanation: "Verbe d’état : s’emploie seul, sans « être ».",
      explanationEn: "Stative verb: used on its own, without \"to be\".",
      example: "Oúnjẹ náà gbóná. (Le repas est chaud.)",
      exampleEn: "Oúnjẹ náà gbóná. (The food is hot.)"
    },
    "jìn": {
      translation: "Profond",
      translationEn: "Deep",
      explanation: "Verbe d’état : s’emploie seul, sans « être ».",
      explanationEn: "Stative verb: used on its own, without \"to be\".",
      example: "Odò yìí jìn. (Cette rivière est profonde.)",
      exampleEn: "Odò yìí jìn. (This river is deep.)",
      audio: "Yo-Jin"
    },
    "tuntun": {
      translation: "Nouveau",
      translationEn: "New",
      explanation: "Se place après le nom.",
      explanationEn: "Comes after the noun.",
      example: "Aṣọ tuntun. (Un vêtement neuf.)",
      exampleEn: "Aṣọ tuntun. (A new garment.)"
    },
    "líle": {
      translation: "Difficile",
      translationEn: "Difficult",
      explanation: "Se place après le nom.",
      explanationEn: "Comes after the noun.",
      example: "Ìdánwò líle. (Un examen difficile.)",
      exampleEn: "Ìdánwò líle. (A difficult exam.)"
    },
    "rọrùn": {
      translation: "Facile",
      translationEn: "Easy",
      explanation: "Verbe d’état : s’emploie seul, sans « être ».",
      explanationEn: "Stative verb: used on its own, without \"to be\".",
      example: "Iṣẹ́ yìí rọrùn. (Ce travail est facile.)",
      exampleEn: "Iṣẹ́ yìí rọrùn. (This work is easy.)"
    },
    "yára": {
      translation: "Rapide",
      translationEn: "Fast",
      explanation: "Verbe d’état : s’emploie seul, sans « être ».",
      explanationEn: "Stative verb: used on its own, without \"to be\".",
      example: "Ó yára. (Il / Elle est rapide.)",
      exampleEn: "Ó yára. (He / She is fast.)"
    },
    "lọ́ra": {
      translation: "Lent",
      translationEn: "Slow",
      explanation: "Verbe d’état : s’emploie seul, sans « être ».",
      explanationEn: "Stative verb: used on its own, without \"to be\".",
      example: "Ó lọ́ra. (Il / Elle est lent(e).)",
      exampleEn: "Ó lọ́ra. (He / She is slow.)"
    },
    "aláriwo": {
      translation: "Bruyant",
      translationEn: "Noisy",
      explanation: "Se place après le nom.",
      explanationEn: "Comes after the noun.",
      example: "Ọmọ aláriwo. (Un enfant bruyant.)",
      exampleEn: "Ọmọ aláriwo. (A noisy child.)"
    },
    "dákẹ́jẹ́ẹ́": {
      translation: "Calme / Silencieux",
      translationEn: "Calm / Quiet",
      explanation: "Verbe d’état : s’emploie seul, sans « être ».",
      explanationEn: "Stative verb: used on its own, without \"to be\".",
      example: "Ọmọ náà dákẹ́jẹ́ẹ́. (L’enfant est resté silencieux.)",
      exampleEn: "Ọmọ náà dákẹ́jẹ́ẹ́. (The child kept quiet.)"
    },
    "gbígbẹ": {
      translation: "Sec",
      translationEn: "Dry",
      explanation: "Se place après le nom.",
      explanationEn: "Comes after the noun.",
      example: "Aṣọ gbígbẹ. (Un vêtement sec.)",
      exampleEn: "Aṣọ gbígbẹ. (A dry garment.)"
    },
    "tútù": {
      translation: "Mouillé",
      translationEn: "Wet",
      explanation: "Verbe d’état : s’emploie seul, sans « être ».",
      explanationEn: "Stative verb: used on its own, without \"to be\".",
      example: "Aṣọ mi tútù. (Mon vêtement est mouillé.)",
      exampleEn: "Aṣọ mi tútù. (My clothes are wet.)"
    },
  },

  // Verbe d'état → adjectif : on redouble la première consonne + une voyelle à ton haut (í, ú).
  // le → líle, ga → gíga, dára → dídára. L'adjectif se place après le nom.
  'Adjectifs — Formation par redoublement': {
    'Le': {
      translation: "(Être) difficile / dur",
      translationEn: "(To be) difficult / hard",
      explanation: "Verbe d'état : s'emploie seul, sans « être ».",
      explanationEn: "Stative verb: used on its own, without \"to be\".",
      example: "Iṣẹ́ yìí le. (Ce travail est difficile.)",
      exampleEn: "Iṣẹ́ yìí le. (This work is hard.)",
      audio: "Yo-Le"
    },
    'Líle': {
      translation: "Difficile",
      translationEn: "Difficult",
      explanation: "Forme adjectivale de « le » : l + í + le. Se place après le nom.",
      explanationEn: "Adjective form of \"le\": l + í + le. Comes after the noun.",
      example: "Ìdánwò líle. (Un examen difficile.)",
      exampleEn: "Ìdánwò líle. (A difficult exam.)",
      audio: "Yo-Lile"
    },
    'Ìdánwò': {
      translation: "Examen",
      translationEn: "Exam",
      explanation: "Nom utilisé pour pratiquer l'adjectif « líle ».",
      explanationEn: "Noun used to practise the adjective \"líle\".",
      example: "Ìdánwò náà le. (L'examen est difficile.)",
      exampleEn: "Ìdánwò náà le. (The exam is difficult.)",
      audio: "Yo-Idanwo"
    },
    'Jìn': {
      translation: "(Être) loin / profond",
      translationEn: "(To be) far / deep",
      explanation: "Verbe d'état : s'emploie seul, sans « être ».",
      explanationEn: "Stative verb: used on its own, without \"to be\".",
      example: "Ilé mi jìn. (Ma maison est loin.)",
      exampleEn: "Ilé mi jìn. (My house is far.)",
      audio: "Yo-Jin"
    },
    'Jíjìn': {
      translation: "Lointain / profond",
      translationEn: "Distant / deep",
      explanation: "Forme adjectivale de « jìn » : j + í + jìn.",
      explanationEn: "Adjective form of \"jìn\": j + í + jìn.",
      example: "Ìlú jíjìn. (Une ville lointaine.)",
      exampleEn: "Ìlú jíjìn. (A distant town.)",
      audio: "Yo-Jijin"
    },
    'Ga': {
      translation: "(Être) grand",
      translationEn: "(To be) tall",
      explanation: "Verbe d'état : s'emploie seul, sans « être ».",
      explanationEn: "Stative verb: used on its own, without \"to be\".",
      example: "Ọmọ náà ga. (L'enfant est grand.)",
      exampleEn: "Ọmọ náà ga. (The child is tall.)",
      audio: "Yo-Ga"
    },
    'Ọmọ gíga': {
      translation: "Enfant grand",
      translationEn: "Tall child",
      explanation: "Nom + adjectif : l'adjectif vient après le nom.",
      explanationEn: "Noun + adjective: the adjective comes after the noun.",
      example: "Ọmọ gíga ni. (C'est un enfant grand.)",
      exampleEn: "Ọmọ gíga ni. (It's a tall child.)",
      audio: "Yo-Omo_giga"
    },
    'Dára': {
      translation: "(Être) bon",
      translationEn: "(To be) good",
      explanation: "Verbe d'état : s'emploie seul, sans « être ».",
      explanationEn: "Stative verb: used on its own, without \"to be\".",
      example: "Oúnjẹ yìí dára. (Ce repas est bon.)",
      exampleEn: "Oúnjẹ yìí dára. (This food is good.)",
      audio: "Yo-Dara"
    },
    'Dáa': {
      translation: "(Être) bon (forme courte)",
      translationEn: "(To be) good (short form)",
      explanation: "Forme courte de « dára », très fréquente à l'oral.",
      explanationEn: "Short form of \"dára\", very common in speech.",
      example: "Ó dáa. (C'est bien.)",
      exampleEn: "Ó dáa. (It's good.)",
      audio: "Yo-Daa"
    },
    'Dídára': {
      translation: "Bon",
      translationEn: "Good",
      explanation: "Forme adjectivale de « dára » : d + í + dára.",
      explanationEn: "Adjective form of \"dára\": d + í + dára.",
      example: "Ọmọ dídára. (Un bon enfant.)",
      exampleEn: "Ọmọ dídára. (A good child.)",
      audio: "Yo-Didara"
    },
    'Wọ́n': {
      translation: "(Être) cher",
      translationEn: "(To be) expensive",
      explanation: "Verbe d'état. À ne pas confondre avec le pronom « wọ́n » (ils).",
      explanationEn: "Stative verb. Not to be confused with the pronoun \"wọ́n\" (they).",
      example: "Ilé yìí wọ́n. (Cette maison est chère.)",
      exampleEn: "Ilé yìí wọ́n. (This house is expensive.)",
      audio: "Yo-Won"
    },
    'Wíwọ́n': {
      translation: "Cher / coûteux",
      translationEn: "Expensive / costly",
      explanation: "Forme adjectivale de « wọ́n » : w + í + wọ́n.",
      explanationEn: "Adjective form of \"wọ́n\": w + í + wọ́n.",
      example: "Ilé wíwọ́n. (Une maison chère.)",
      exampleEn: "Ilé wíwọ́n. (An expensive house.)",
      audio: "Yo-Wiwon"
    },
    'Ilé wíwọ́n': {
      translation: "Maison chère",
      translationEn: "Expensive house",
      explanation: "Nom + adjectif : l'adjectif vient après le nom.",
      explanationEn: "Noun + adjective: the adjective comes after the noun.",
      example: "Ilé wíwọ́n ni. (C'est une maison chère.)",
      exampleEn: "Ilé wíwọ́n ni. (It's an expensive house.)",
      audio: "Yo-Ile_wiwon"
    },
    'Burú': {
      translation: "(Être) mauvais",
      translationEn: "(To be) bad",
      explanation: "Verbe d'état, contraire de « dára ».",
      explanationEn: "Stative verb, opposite of \"dára\".",
      example: "Ojú ọjọ́ burú. (Il fait mauvais temps.)",
      exampleEn: "Ojú ọjọ́ burú. (The weather is bad.)",
      audio: "Yo-Buru"
    },
    'Búburú': {
      translation: "Mauvais",
      translationEn: "Bad",
      explanation: "Forme adjectivale de « burú » : b + ú + burú (la voyelle redoublée suit celle du verbe).",
      explanationEn: "Adjective form of \"burú\": b + ú + burú (the doubled vowel follows the verb's).",
      example: "Ìwà búburú. (Un mauvais comportement.)",
      exampleEn: "Ìwà búburú. (Bad behaviour.)",
      audio: "Yo-Buburu"
    },
    'Kúrú': {
      translation: "(Être) court",
      translationEn: "(To be) short",
      explanation: "Verbe d'état : s'emploie seul, sans « être ».",
      explanationEn: "Stative verb: used on its own, without \"to be\".",
      example: "Aṣọ yìí kúrú. (Ce vêtement est court.)",
      exampleEn: "Aṣọ yìí kúrú. (This garment is short.)",
      audio: "Yo-Kuru"
    },
    'Kúkurú': {
      translation: "Court",
      translationEn: "Short",
      explanation: "Forme adjectivale de « kúrú » : k + ú + kurú.",
      explanationEn: "Adjective form of \"kúrú\": k + ú + kurú.",
      example: "Ọ̀nà kúkurú. (Un chemin court.)",
      exampleEn: "Ọ̀nà kúkurú. (A short path.)",
      audio: "Yo-Kukuru"
    },
    'Pọ̀': {
      translation: "(Être) nombreux",
      translationEn: "(To be) many / plenty",
      explanation: "Verbe d'état : s'emploie seul, sans « être ».",
      explanationEn: "Stative verb: used on its own, without \"to be\".",
      example: "Èèyàn pọ̀ níbí. (Il y a beaucoup de monde ici.)",
      exampleEn: "Èèyàn pọ̀ níbí. (There are many people here.)",
      audio: "Yo-Po"
    },
    'Púpọ̀': {
      translation: "Beaucoup / nombreux",
      translationEn: "A lot / many",
      explanation: "Forme adjectivale de « pọ̀ » : p + ú + pọ̀.",
      explanationEn: "Adjective form of \"pọ̀\": p + ú + pọ̀.",
      example: "Owó púpọ̀. (Beaucoup d'argent.)",
      exampleEn: "Owó púpọ̀. (A lot of money.)",
      audio: "Yo-Pupo"
    },
  },

  // Le goût et la texture des aliments : verbes d’état, s’emploient sans « être ».
  'Adjectifs — Les goûts': {
    "ta": {
      translation: "Épicé / piquant",
      translationEn: "Spicy / hot",
      explanation: "Verbe d’état : « ta » = être épicé, piquant. Même mot que « ta » (vendre) : le contexte fait la différence.",
      explanationEn: "Stative verb: \"ta\" = to be spicy, hot. Same word as \"ta\" (to sell): context tells them apart.",
      example: "Ata yìí ta. (Ce piment est piquant.)",
      exampleEn: "Ata yìí ta. (This pepper is hot.)",
      audio: "Yo-Ta"
    },
    "dùn": {
      translation: "Bon / sucré",
      translationEn: "Tasty / sweet",
      explanation: "Verbe d’état : « dùn » = être bon, sucré, agréable au goût.",
      explanationEn: "Stative verb: \"dùn\" = to be tasty, sweet, pleasant to taste.",
      example: "Oúnjẹ yìí dùn. (Ce plat est bon.)",
      exampleEn: "Oúnjẹ yìí dùn. (This food is tasty.)",
      audio: "Yo-Dun-sucre"
    },
    "korò": {
      translation: "Amer",
      translationEn: "Bitter",
      explanation: "Verbe d’état : « korò » = être amer.",
      explanationEn: "Stative verb: \"korò\" = to be bitter.",
      example: "Àgbo yẹn korò. (Cette tisane est amère.)",
      exampleEn: "Àgbo yẹn korò. (That herbal drink is bitter.)",
      audio: "Yo-Koro"
    },
    "fẹ́lẹ́": {
      translation: "Léger / moelleux",
      translationEn: "Light / soft",
      explanation: "Verbe d’état : « fẹ́lẹ́ » = être léger, moelleux (pour une pâte comme l’àmàlà).",
      explanationEn: "Stative verb: \"fẹ́lẹ́\" = to be light, soft (for a dough like àmàlà).",
      example: "Àmàlà yìí fẹ́lẹ́. (Cet amala est moelleux.)",
      exampleEn: "Àmàlà yìí fẹ́lẹ́. (This amala is soft.)",
      audio: "Yo-Fele-moelleux"
    },
    "le": {
      translation: "Dur",
      translationEn: "Hard",
      explanation: "Verbe d’état : « le » = être dur. Forme adjectivale par redoublement : líle.",
      explanationEn: "Stative verb: \"le\" = to be hard. Adjective form by reduplication: líle.",
      example: "Iyán tí mo rà le. (L’igname pilée que j’ai achetée était dure.)",
      exampleEn: "Iyán tí mo rà le. (The pounded yam I bought was hard.)",
      audio: "Yo-Le-dur"
    }
  },

  'Conjonctions': {
    'àti': {
      translation: "Et (entre deux noms)",
      translationEn: "And (between nouns)",
      explanation: "Relie deux noms. Entre deux verbes, on utilise « sì ».",
      explanationEn: "Links two nouns. Between two verbs, use \"sì\".",
      example: "Ìyá àti bàbá. (Maman et papa.)",
      exampleEn: "Ìyá àti bàbá. (Mother and father.)"
    },
    'sì': {
      translation: "Et (entre deux actions)",
      translationEn: "And (between actions)",
      explanation: "Se place devant le deuxième verbe.",
      explanationEn: "Comes before the second verb.",
      example: "Mo jẹun, mo sì mu omi. (J'ai mangé et j'ai bu de l'eau.)",
      exampleEn: "Mo jẹun, mo sì mu omi. (I ate and drank water.)"
    },
    'ṣùgbọ́n': {
      translation: "Mais",
      translationEn: "But",
      explanation: "Marque une opposition.",
      explanationEn: "Shows a contrast.",
      example: "Mo fẹ́ lọ, ṣùgbọ́n ó ti pẹ́. (Je veux partir, mais il est tard.)",
      exampleEn: "Mo fẹ́ lọ, ṣùgbọ́n ó ti pẹ́. (I want to go, but it's late.)"
    },
    'tàbí': {
      translation: "Ou",
      translationEn: "Or",
      explanation: "Propose un choix.",
      explanationEn: "Offers a choice.",
      example: "Tíì tàbí kọfí? (Thé ou café ?)",
      exampleEn: "Tíì tàbí kọfí? (Tea or coffee?)"
    },
    'nítorí pé': {
      translation: "Parce que",
      translationEn: "Because",
      explanation: "Introduit une cause.",
      explanationEn: "Introduces a reason.",
      example: "Mi ò lọ nítorí pé ara mi ò yá. (Je ne suis pas allé parce que j'étais malade.)",
      exampleEn: "Mi ò lọ nítorí pé ara mi ò yá. (I didn't go because I was ill.)"
    },
    'tí': {
      translation: "Si / Quand",
      translationEn: "If / When",
      explanation: "Introduit une condition ou un moment. On dit aussi « bí ».",
      explanationEn: "Introduces a condition or a time. \"Bí\" is also used.",
      example: "Tí o bá dé, pè mí. (Quand tu arrives, appelle-moi.)",
      exampleEn: "Tí o bá dé, pè mí. (When you arrive, call me.)"
    },
  },

  'Adverbes': {
    'púpọ̀': {
      translation: "Beaucoup",
      translationEn: "A lot / Many",
      explanation: "Se place après le verbe ou le nom.",
      explanationEn: "Comes after the verb or the noun.",
      example: "Ó jẹun púpọ̀. (Il/Elle a beaucoup mangé.)",
      exampleEn: "Ó jẹun púpọ̀. (He/She ate a lot.)"
    },
    'díẹ̀': {
      translation: "Un peu",
      translationEn: "A little",
      explanation: "Redoublé, « díẹ̀díẹ̀ » veut dire « doucement, petit à petit ».",
      explanationEn: "Doubled, \"díẹ̀díẹ̀\" means \"slowly, little by little\".",
      example: "Mo gbọ́ Yorùbá díẹ̀. (Je comprends un peu le yoruba.)",
      exampleEn: "Mo gbọ́ Yorùbá díẹ̀. (I understand a little Yoruba.)"
    },
    'gan-an': {
      translation: "Vraiment / Très",
      translationEn: "Really / Very",
      explanation: "Renforce le mot qui précède.",
      explanationEn: "Strengthens the word before it.",
      example: "Ó dára gan-an. (C'est vraiment bien.)",
      exampleEn: "Ó dára gan-an. (It's really good.)"
    },
    'dáadáa': {
      translation: "Bien",
      translationEn: "Well",
      explanation: "Décrit la manière.",
      explanationEn: "Describes how something is done.",
      example: "Ó ń kọrin dáadáa. (Il/Elle chante bien.)",
      exampleEn: "Ó ń kọrin dáadáa. (He/She sings well.)"
    },
    'kíákíá': {
      translation: "Vite",
      translationEn: "Quickly",
      explanation: "Décrit la vitesse.",
      explanationEn: "Describes speed.",
      example: "Wá kíákíá! (Viens vite !)",
      exampleEn: "Wá kíákíá! (Come quickly!)"
    },
    'nísinsìnyí': {
      translation: "Maintenant",
      translationEn: "Now",
      explanation: "Souvent en fin de phrase.",
      explanationEn: "Often at the end of the sentence.",
      example: "Mo ń lọ nísinsìnyí. (Je pars maintenant.)",
      exampleEn: "Mo ń lọ nísinsìnyí. (I am leaving now.)"
    },
  },
};

export default grammaire;
